// src/components/UserDashboard/FriendList.js - UPDATED WITH AVATAR FALLBACK AND SIMPLIFIED CHAT INTERFACE
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  FaUserFriends,
  FaComments,
  FaCommentDots,
  FaPaperPlane,
  FaSpinner,
  FaExclamationTriangle,
  FaArrowLeft
} from 'react-icons/fa';
import '../styles/friend-chat.css';

const API_URL = process.env.REACT_APP_API_URL;

// Avatar Fallback Component with colored background
const AvatarFallback = ({ name, size = 'normal', className = '' }) => {
  // Generate a consistent color based on name
  const getAvatarColor = (name) => {
    if (!name) return '#999';
    
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
      '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
    ];
    
    // Use first letter to determine color
    const charCode = name.charAt(0).toUpperCase().charCodeAt(0);
    const colorIndex = charCode % colors.length;
    return colors[colorIndex];
  };

  const backgroundColor = getAvatarColor(name);
  const firstLetter = name ? name.charAt(0).toUpperCase() : 'U';
  
  const sizeClass = size === 'small' ? 'avatar-fallback-small' : 
                   size === 'large' ? 'avatar-fallback-large' : 
                   'avatar-fallback-normal';

  return (
    <div 
      className={`avatar-fallback ${sizeClass} ${className}`}
      style={{ backgroundColor }}
    >
      <span className="avatar-letter">{firstLetter}</span>
    </div>
  );
};

// Individual Friend Avatar Component
const FriendAvatar = ({ friend, size = 'normal', className = '', onClick }) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className={`friend-avatar-container ${className}`} onClick={onClick}>
      {friend?.avatar_url && !imageError ? (
        <img
          src={friend.avatar_url}
          alt={friend.name || 'Friend'}
          className={`friend-avatar ${size === 'small' ? 'small' : size === 'large' ? 'large' : ''}`}
          onError={handleImageError}
        />
      ) : (
        <AvatarFallback 
          name={friend?.name || 'Unknown'} 
          size={size} 
          className={`friend-avatar ${size === 'small' ? 'small' : size === 'large' ? 'large' : ''}`}
        />
      )}
    </div>
  );
};

const FriendList = ({ 
  friends: propFriends = [], 
  navigate, 
  limit = Infinity, 
  user, 
  language = 'th',
  onFriendSelect,
  showChat,
  selectedFriend,
  onBackToFriends,
  isMobile = false
}) => {
  const [friends, setFriends] = useState(propFriends);
  const [loading, setLoading] = useState(!propFriends.length);
  const [error, setError] = useState(null);

  // Chat states
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef(null);

  const translations = {
    th: {
      friendList: 'รายชื่อเพื่อน',
      noFriends: 'ยังไม่มีเพื่อน',
      searchFriends: 'ค้นหาเพื่อน',
      loadingFriends: 'กำลังโหลดรายชื่อเพื่อน...',
      loadingChat: 'กำลังโหลดการสนทนา...',
      noMessages: 'ยังไม่มีข้อความ เริ่มการสนทนากันเลย!',
      typeMessage: 'พิมพ์ข้อความ...',
      tryAgain: 'ลองอีกครั้ง',
      backToFriends: 'กลับไปรายชื่อเพื่อน',
      showAllFriends: 'ดูเพื่อนทั้งหมด',
      selectFriend: 'กรุณาเลือกเพื่อนเพื่อเริ่มการสนทนา',
      chat: 'แชท'
    },
    en: {
      friendList: 'Friend List',
      noFriends: 'No friends yet',
      searchFriends: 'Search Friends',
      loadingFriends: 'Loading friend list...',
      loadingChat: 'Loading conversation...',
      noMessages: 'No messages yet. Start a conversation!',
      typeMessage: 'Type a message...',
      tryAgain: 'Try Again',
      backToFriends: 'Back to Friends',
      showAllFriends: 'Show All Friends',
      selectFriend: 'Please select a friend to start conversation',
      chat: 'Chat'
    },
    zh: {
      friendList: '好友列表',
      noFriends: '还没有好友',
      searchFriends: '搜索好友',
      loadingFriends: '正在加载好友列表...',
      loadingChat: '正在加载对话...',
      noMessages: '还没有消息。开始对话吧！',
      typeMessage: '输入消息...',
      tryAgain: '重试',
      backToFriends: '返回好友列表',
      showAllFriends: '显示所有好友',
      selectFriend: '请选择一个好友开始对话',
      chat: '聊天'
    }
  };

  const t = translations[language];

  // Use prop friends if available, otherwise fetch
  useEffect(() => {
    if (propFriends.length > 0) {
      setFriends(propFriends);
      setLoading(false);
    } else {
      fetchFriends();
    }
  }, [propFriends]);

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const fetchFriends = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/friends`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      const friendsData = response.data?.friends || [];
      const uniqueFriends = friendsData.filter((friend, index, self) =>
        index === self.findIndex(f => f.id === friend.id)
      );

      setFriends(uniqueFriends);
    } catch (err) {
      console.error(err);
      setError('ไม่สามารถโหลดรายชื่อเพื่อนได้');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = useCallback(async (convId) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/conversations/${convId}/messages`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setMessages(data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const markMessagesAsRead = useCallback((convId) =>
    axios.put(
      `${API_URL}/conversations/${convId}/read`,
      {},
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    ).catch(console.error), []);

  const findOrCreateConversation = useCallback(async (friend) => {
    try {
      setChatLoading(true);

      const { data } = await axios.post(
        `${API_URL}/conversations/find-or-create`,
        { participantId: friend.id, type: 'direct' },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (data?.conversationId) {
        setConversationId(data.conversationId);
        await fetchMessages(data.conversationId);
        await markMessagesAsRead(data.conversationId);
      }
    } catch (err) {
      console.error(err);
      alert('ไม่สามารถเริ่มการสนทนาได้ โปรดลองอีกครั้ง');
    } finally {
      setChatLoading(false);
    }
  }, [fetchMessages, markMessagesAsRead]);

  useEffect(() => {
    if (showChat && selectedFriend) {
      findOrCreateConversation(selectedFriend);
    }
  }, [showChat, selectedFriend, findOrCreateConversation]);

  useEffect(() => {
    if (showChat) scrollToBottom();
  }, [messages, showChat]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !conversationId) return;

    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/conversations/${conversationId}/messages`,
        { content: newMessage.trim(), message_type: 'text' },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (data) {
        setMessages(prev => [...prev, data]);
        setNewMessage('');
        scrollToBottom();
      }
    } catch (err) {
      console.error(err);
      alert('ไม่สามารถส่งข้อความได้ โปรดลองอีกครั้ง');
    } finally { 
      setSending(false); 
    }
  };

  const handleFriendClick = (friend) => {
    if (onFriendSelect) {
      onFriendSelect(friend);
    }
  };

  const formatMessageTime = (ts) => {
    const date = new Date(ts);
    const diffH = (Date.now() - date) / 36e5;
    return diffH < 24
      ? date.toLocaleTimeString(language === 'th' ? 'th-TH' : 'en-US', { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString(language === 'th' ? 'th-TH' : 'en-US', {
          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
        });
  };

  const renderFriendsView = () => {
    if (loading) return (
      <div className="loading-container">
        <div className="loading-spinner-small" />
        <span>{t.loadingFriends}</span>
      </div>
    );

    if (error) return (
      <div className="error-container">
        <FaExclamationTriangle />
        <p className="error-message">{error}</p>
        <button className="btn btn-secondary btn-sm" onClick={fetchFriends}>
          {t.tryAgain}
        </button>
      </div>
    );

    if (!friends.length) return (
      <div className="no-data-container">
        <FaUserFriends />
        <p className="no-data">{t.noFriends}</p>
        <button className="btn btn-primary" onClick={() => navigate('/search')}>
          {t.searchFriends}
        </button>
      </div>
    );

    const displayFriends = friends.slice(0, limit);
    return (
      <div className="friend-list">
        {displayFriends.map(friend => (
          <div
            key={friend.id}
            className={`friend-item ${selectedFriend?.id === friend.id ? 'active' : ''}`}
            onClick={() => handleFriendClick(friend)}
          >
            <FriendAvatar friend={friend} size="normal" />
            <div className="friend-info">
              <h4>{friend.name}</h4>
              {friend.email && <p className="friend-email">{friend.email}</p>}
            </div>
            <div className="friend-actions">
              <FaCommentDots />
            </div>
          </div>
        ))}
        {friends.length > limit && (
          <div className="show-more-container">
            <button className="btn btn-link" onClick={() => navigate('/friends')}>
              {t.showAllFriends} ({friends.length})
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderChatView = () => {
    if (!selectedFriend) return (
      <div className="no-data-container">
        <FaComments />
        <p className="no-data">{t.selectFriend}</p>
        <button className="btn btn-primary" onClick={onBackToFriends}>
          {t.backToFriends}
        </button>
      </div>
    );

    if (chatLoading) return (
      <div className="loading-container">
        <div className="loading-spinner-small" />
        <span>{t.loadingChat}</span>
      </div>
    );

    return (
      <div className="chat-container">
        {/* Messages */}
        <div className="chat-messages">
          {messages.length ? (
            <>
              {messages.map(msg => (
                <div
                  key={msg.id}
                  className={`message ${msg.sender_id === user?.id ? 'sent' : 'received'}`}
                >
                  {msg.sender_id !== user?.id && (
                    <FriendAvatar 
                      friend={selectedFriend} 
                      size="small" 
                      className="message-avatar"
                    />
                  )}
                  <div className="message-content">
                    <div className="message-bubble"><p>{msg.content}</p></div>
                    <div className="message-time">{formatMessageTime(msg.created_at)}</div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </>
          ) : (
            <div className="no-messages">
              <FaComments />
              <p>{t.noMessages}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="friend-list-container">
      {/* Header only for desktop or when not in mobile dashboard */}
      {!isMobile && (
        <div className="friend-list-header">
          <h2>
            {showChat && selectedFriend ? (
              <>
                <button 
                  className="back-button" 
                  onClick={onBackToFriends}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#000',
                    fontSize: '1.1rem',
                    marginRight: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0',
                    margin: '0'
                  }}
                >
                  <FaArrowLeft />
                </button>
                <FriendAvatar 
                  friend={selectedFriend} 
                  size="small" 
                  className="chat-header-avatar"
                />
                {selectedFriend?.name || t.chat}
              </>
            ) : (
              <><FaUserFriends /> {t.friendList}</>
            )}
          </h2>
        </div>
      )}

      {/* Content */}
      <div className="friend-list-content">
        {showChat ? renderChatView() : renderFriendsView()}
      </div>

      {/* Chat Input - Fixed at bottom above bottom navigation */}
      {showChat && selectedFriend && !chatLoading && (
        <div className="chat-input-container chat-input-fixed-bottom">
          <form onSubmit={sendMessage} className="chat-input-form">
            <div className="input-wrapper">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t.typeMessage}
                className="message-input"
                disabled={sending}
              />
              <button
                type="submit"
                className={`send-button ${newMessage.trim() ? 'active' : ''}`}
                disabled={!newMessage.trim() || sending}
              >
                {sending ? <FaSpinner className="icon-spin" /> : <FaPaperPlane />}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FriendList;